import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,
  createMarkupEditorToolStyles,
  locale_en_gb,
  markup_editor_defaults,
  markup_editor_locale_en_gb,
  PinturaEditorOptions,
  plugin_annotate,
  plugin_annotate_locale_en_gb,
  setPlugins,
  Shape,
  ShapeToolOptions,
} from '@pqina/pintura';
import { catchError, tap, firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from '../../../../shared/services/loader/loader.service';
import { TemplateService } from '../../../../shared/services/template/template-service';
import { Constants, UploadOperation } from '../../../../shared/constants/common';
import { GlobalService } from '../../../../shared/services/global/global-service';
import { UserPreferencesService } from '../../../../shared/services/user-preferences/user-preferences.service';
import { UploadMediaRequest } from '../../../../shared/models/template.model';
import { UserPreferencesData } from '../../../../shared/models/user-preferences.model';

setPlugins(plugin_annotate);

@Component({
  selector: 'app-template-image-edit',
  standalone: false,
  templateUrl: './template-image-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './template-image-edit.component.scss',
})
export class TemplateImageEditComponent implements OnInit {
  dialogRef = inject<MatDialogRef<TemplateImageEditComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private templateService = inject(TemplateService);
  private toastrService = inject(ToastrService);
  private loaderService = inject(LoaderService);
  private globalService = inject(GlobalService);
  private userPreferencesService = inject(UserPreferencesService);

  userPreferencesData: UserPreferencesData;
  recordId: number;
  moduleName: string;
  itemCode: string;
  sectionNo: number | undefined;
  mediaSection: string;
  imageData = '';
  editorOptions: PinturaEditorOptions = {
    imageReader: createDefaultImageReader(),
    imageWriter: createDefaultImageWriter(),
    shapePreprocessor: createDefaultShapePreprocessor(),
    ...markup_editor_defaults,
    locale: {
      ...locale_en_gb,
      ...plugin_annotate_locale_en_gb,
      ...markup_editor_locale_en_gb,
    },
  };
  markupEditorToolStyles: ReturnType<typeof createMarkupEditorToolStyles> = createMarkupEditorToolStyles();
  shape: object;
  localObj: Record<string, Record<string, unknown>> = {};

  async ngOnInit() {
    this.userPreferencesData = this.userPreferencesService.getUserPreferencesData();
    this.recordId = this.data?.recordId;
    this.moduleName = this.data?.moduleName;
    this.itemCode = this.data?.itemCode;
    this.sectionNo = this.data?.sectionNo;
    this.mediaSection = this.data?.mediaSection;
    const s3Url = this.data?.image; // Replace with your S3 URL

    try {
      this.loaderService.showLoader();
      const blob = await firstValueFrom(this.templateService.downloadImage(s3Url));
      this.imageData = URL.createObjectURL(blob);
      this.loaderService.hideLoader();
    } catch (error) {
      this.loaderService.hideLoader();
    }


    this.markupEditorToolStyles = {
      ...this.markupEditorToolStyles,
      sharpie: (this.markupEditorToolStyles.sharpie ?? []).map((sharpie) => {
        const localObjSharpie = this.localObj['sharpie'];
        return localObjSharpie ? { ...sharpie, ...localObjSharpie } : sharpie;
      }) as unknown as [Shape, ShapeToolOptions],
      path: (this.markupEditorToolStyles['path'] ?? []).map((pathItem) => {
        const localObjPath = this.localObj['path'];
        return localObjPath ? { ...pathItem, ...localObjPath } : pathItem;
      }) as unknown as [Shape, ShapeToolOptions],
      line: (this.markupEditorToolStyles.line ?? []).map((line) => {
        const localObjLine = this.localObj['line'];
        return localObjLine ? { ...line, ...localObjLine } : line;
      }) as unknown as [Shape, ShapeToolOptions],
      arrow: (this.markupEditorToolStyles.arrow ?? []).map((arrow) => {
        const localObjArrow = this.localObj['arrow'];
        return localObjArrow ? { ...arrow, ...localObjArrow } : arrow;
      }) as unknown as [Shape, ShapeToolOptions],
      rectangle: (this.markupEditorToolStyles.rectangle ?? []).map((rectangle) => {
        const localObjRect = this.localObj['rectangle'];
        return localObjRect ? { ...rectangle, ...localObjRect } : rectangle;
      }) as unknown as [Shape, ShapeToolOptions],
      ellipse: (this.markupEditorToolStyles.ellipse ?? []).map((ellipse) => {
        const localObjEllipse = this.localObj['ellipse'];
        return localObjEllipse ? { ...ellipse, ...localObjEllipse } : ellipse;
      }) as unknown as [Shape, ShapeToolOptions],
      // Set fontSize at the top level of the shape style — NOT nested under a `text` key.
      // Pintura's `text` property on a shape holds the string content, so nesting
      // style props there causes `shapeFormatHTMLText` to receive an object and crash
      // with "text2.replace is not a function".
      text: (this.markupEditorToolStyles.text ?? []).map((textObj) => {
        const localObjText = this.localObj['text'];
        const withFontSize = { ...textObj, fontSize: '8%' };
        return localObjText ? { ...withFontSize, ...localObjText } : withFontSize;
      }) as unknown as [Shape, ShapeToolOptions],
    };
  }

  selectControl(event: unknown): void {
    this.shape = event as object;
  }

  handleInlineLoad($event: unknown): void {}

  handleInlineProcess($event: { dest?: File }): void {
    const reader = new FileReader();
    reader.onload = async () => {
      const base64String = (reader.result as string).split(',')[1];
      const file = this.base64ToFile(
        base64String,
        'updated-image.png',
        'image/png'
      );
      this.uploadUpdatedImage(file);
    };
    reader.readAsDataURL($event.dest);
  }

  handleEditorSelectStyle(shape: Record<string, unknown>): void {
    const shapeKey = Object.keys(shape)[0];
  }

  base64ToFile(base64String: string, fileName: string, mimeType: string): File {
    const byteString = atob(base64String);
    const byteNumbers = new Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteNumbers[i] = byteString.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], fileName, { type: mimeType });
  }

  uploadUpdatedImage(file: File): void {
    const request: UploadMediaRequest = {
      recordId: this.recordId,
      moduleName: this.moduleName,
      referenceId: '0',
      referenceTable: this.mediaSection == Constants.MediaType.ImageField
                    ? Constants.MediaType.ImageField
                    : Constants.MediaType.Media,
      itemCode: this.itemCode,
      sectionNo: this.sectionNo,
      file: file,
      mediaKey: this.data.imageKey,
      action: UploadOperation.Upload,
      companyId: this.userPreferencesData.selectedCompanyId,
      uploadBy: this.userPreferencesData.userId,
      uploadedByName: this.userPreferencesData.userName
    };

    this.loaderService.showLoader('Uploading...');
    this.globalService.uploadMedia(request)
      .pipe(
        tap((response) => {
          if (response.errors) {
            this.toastrService.error('Error while uploading file');
          } else {
            this.toastrService.success('File updated successfully');
          }
          this.loaderService.hideLoader();
          this.back(response);
        }),
        catchError((err: unknown) => {
          console.error('Error while uploading file:', err);
          this.loaderService.hideLoader();
          throw err;
        })
      )
      .subscribe();
  }

  handleCloseEvent(): void {
    this.back();
  }

  back(response?: Record<string, unknown>): void {
    this.dialogRef.close(response);
  }
}
